import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from 'semantic-ui-react';
import './OriginalImageSection.css';
import { selectCurrentPersonImageId, selectSelectedVTOResultId } from '../../../redux/slices/vtoSlice';
import { selectPersonImage, selectOriginalImage, selectIsVirtualTryOnResult, selectMetadata, selectVirtualTryOnMetadata } from '../../../redux/selectors/personImageSelectors';
import { usePersonImages } from '../../../hooks/usePersonImages';
import { usePersonImage } from '../../../hooks/usePersonImage';
import { useVTOResults } from '../../../hooks/useVTOResults';
import { useCredits } from '../../../hooks/useCredits';
import { useTogglePersonImageBackgroundMutation, useRemovePersonBgFromUploadMutation } from '../../../redux/apis/endpoints/personImageApi';
import { useToggleVTOResultBackgroundMutation, useRemoveVTOResultBackgroundMutation } from '../../../redux/apis/endpoints/vtoResultApi';
import { compressForBackgroundRemoval } from '../../../utils/imageCompression';

const OriginalImageSection = forwardRef(({ onUpdateImage, onBgRemovalStart, onBgRemovalEnd }, ref) => {
  const [isToggling, setIsToggling] = useState(false);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const currentPersonImageId = useSelector(selectCurrentPersonImageId);
  const currentPersonImage = useSelector(selectPersonImage);
  const personOriginalImage = useSelector(selectOriginalImage);
  const isVTOResult = useSelector(selectIsVirtualTryOnResult);
  const metadata = useSelector(selectMetadata);
  const vtoMetadata = useSelector(selectVirtualTryOnMetadata);
  const selectedVTOResultId = useSelector(selectSelectedVTOResultId);

  // Get person image data from RTK Query
  const { activePersonImage, getPersonImageById, isCanvasIntegrated, setActivePersonImage, refetchImages } = usePersonImages();
  const vtoOriginalImage = currentPersonImageId ? getPersonImageById(currentPersonImageId) : null;

  // Get person image hook for canvas updates
  const personImageState = usePersonImage();

  // Get VTO result data
  const { getVTOResultById } = useVTOResults();
  const selectedVTOResult = selectedVTOResultId ? getVTOResultById(selectedVTOResultId) : null;

  // Credits hook
  const { refetch: refetchCredits } = useCredits();

  // Mutations
  const [togglePersonImageBackground, { isLoading: isTogglingPerson }] = useTogglePersonImageBackgroundMutation();
  const [toggleVTOResultBackground, { isLoading: isTogglingVTO }] = useToggleVTOResultBackgroundMutation();
  const [removeVTOResultBackground, { isLoading: isRemovingVTOBg }] = useRemoveVTOResultBackgroundMutation();
  const [removePersonBgFromUpload] = useRemovePersonBgFromUploadMutation();

  // Priority order for finding the original image:
  // 1. Active person image from backend (respect useRemovedImage flag)
  // 2. VTO slice originalPersonImage (set when VTO generation starts)
  // 3. Original from VTO metadata (passed during history selection)
  // 4. Original from standard metadata
  // 5. Person image slice originalImage (set when image is first uploaded or VTO applied)
  // 6. Current person image if it's not a VTO result
  // IMPORTANT: Only show image if canvas integration is enabled
  const getImageUrl = (img) => {
    if (!img) return null;

    // Respect useRemovedImage flag: if true and removedBgImage exists, use it; otherwise use original
    const shouldUseRemoved = img.useRemovedImage !== false && img.removedBgImage?.url;
    if (shouldUseRemoved) return img.removedBgImage.url;

    return img.image?.url || img.url || img.ImageUrl || img.Image?.url;
  };

  const imageToShow = isCanvasIntegrated ? (
    getImageUrl(selectedVTOResult?.person_image) ||
    getImageUrl(selectedVTOResult?.personImage) ||
    vtoMetadata?.originalPersonImage ||
    getImageUrl(activePersonImage) ||
    getImageUrl(vtoOriginalImage) ||
    metadata?.originalPersonImage ||
    personOriginalImage ||
    (!isVTOResult ? currentPersonImage : null)
  ) : null;

  const handleImageClick = () => {
    if (onUpdateImage) {
      onUpdateImage();
    }
  };

  const handleToggleBackground = async () => {
    if (isToggling) return;

    console.log('🔄 Toggle background clicked', {
      isVTOResult,
      selectedVTOResultId: selectedVTOResult?.id,
      hasVTORemovedBg: !!selectedVTOResult?.removedBgImage,
      activePersonImageId: activePersonImage?.id,
      hasPersonRemovedBg: !!activePersonImage?.removedBgImage
    });

    try {
      setIsToggling(true);

      // Determine the target state (what we want to toggle TO)
      const currentlyUsingRemoved = activePersonImage?.useRemovedImage !== false;
      const targetState = !currentlyUsingRemoved; // Toggle to opposite

      console.log('🎯 Target state:', { currentlyUsingRemoved, targetState });

      // Handle VTO result
      if (selectedVTOResult?.id) {
        const vtoCurrentState = selectedVTOResult.useRemovedImage !== false;

        if (!selectedVTOResult.removedBgImage && targetState) {
          // Need to create removed background first
          console.log('🔄 VTO result needs background removal first');
          const vtoResult = await removeVTOResultBackground(selectedVTOResult.id).unwrap();
          console.log('✅ VTO result background removed:', vtoResult);

          // After creation, the backend sets useRemovedImage to true
          // Update canvas to show the removed background version
          if (vtoResult.removedBgImage?.url && isCanvasIntegrated) {
            console.log('🖼️ Updating canvas with removed background VTO result');
            await personImageState.setVirtualTryOnResult({
              image: vtoResult.removedBgImage.url,
              originalImage: activePersonImage?.image?.url || activePersonImage?.removedBgImage?.url,
              metadata: {
                isVirtualTryOnResult: true,
                vtoResultId: vtoResult.id
              }
            });
            console.log('✅ Canvas updated with removed background');
          }

          // If targetState is false, we need to toggle it back
          if (!targetState) {
            await toggleVTOResultBackground(selectedVTOResult.id).unwrap();
            console.log('✅ VTO result toggled to match person image');
          }
        } else if (selectedVTOResult.removedBgImage && vtoCurrentState !== targetState) {
          // VTO state doesn't match target, toggle it
          console.log('✅ Will toggle VTO result to match:', selectedVTOResult.id, 'from', vtoCurrentState, 'to', targetState);
          const vtoResult = await toggleVTOResultBackground(selectedVTOResult.id).unwrap();

          // Update canvas to reflect the toggle
          if (isCanvasIntegrated) {
            const removedUrl = Array.isArray(vtoResult.removedBgImage) ? vtoResult.removedBgImage[0]?.url : vtoResult.removedBgImage?.url;
            const resUrl = Array.isArray(vtoResult.resultImage) ? vtoResult.resultImage[0]?.url : vtoResult.resultImage?.url;
            const imageUrl = vtoResult.useRemovedImage ? removedUrl : resUrl;
            if (imageUrl) {
              console.log('🖼️ Updating canvas with toggled VTO result');
              await personImageState.setVirtualTryOnResult({
                image: imageUrl,
                originalImage: activePersonImage?.image?.url || activePersonImage?.removedBgImage?.url,
                metadata: {
                  isVirtualTryOnResult: true,
                  vtoResultId: vtoResult.id
                }
              });
              console.log('✅ Canvas updated with toggled VTO result');
            }
          }
        }
      }

      // Always toggle person image if it has removed background
      if (activePersonImage?.id && activePersonImage?.removedBgImage) {
        console.log('✅ Will toggle person image:', activePersonImage.id);
        const result = await togglePersonImageBackground(activePersonImage.id).unwrap();

        // Update canvas if NO VTO result is selected (showing original person image)
        if (!selectedVTOResult?.id && isCanvasIntegrated) {
          console.log('🖼️ Updating canvas with toggled person image');
          const imageUrl = result.useRemovedImage ? result.removedBgImage?.url : result.image?.url;
          if (imageUrl) {
            // Re-select the person image to update the canvas
            await setActivePersonImage(result.id);
            console.log('✅ Canvas updated with toggled image:', imageUrl);
          }
        }
      }

      console.log('✅ All toggles completed');
    } catch (error) {
      console.error('Toggle background error:', error);
    } finally {
      setIsToggling(false);
    }
  };

  // Determine if toggle button should be shown
  // Show toggle if either person image or VTO result has removed background
  const canToggle = activePersonImage?.removedBgImage || selectedVTOResult?.removedBgImage;

  // Check if we need to do first-time BG removal (no removed BG exists yet)
  // For VTO result: check if VTO result has no removedBgImage
  // For person image: check if person image has no removedBgImage
  const needsBgRemoval = selectedVTOResult?.id
    ? !selectedVTOResult?.removedBgImage
    : !activePersonImage?.removedBgImage;

  // Use person image's useRemovedImage flag - only true if explicitly true
  const useRemovedImage = selectedVTOResult?.id
    ? selectedVTOResult?.useRemovedImage === true
    : activePersonImage?.useRemovedImage === true;

  // Handle first-time background removal (costs 1 credit)
  // If VTO result is selected, remove BG from VTO result AND person image
  // Otherwise, remove BG from person image only
  const handleRemoveBackground = async () => {
    if (isRemovingBg) return;

    try {
      setIsRemovingBg(true);
      onBgRemovalStart?.();

      // If VTO result is selected, remove BG from VTO result
      if (selectedVTOResult?.id) {
        console.log('🔄 Removing background from VTO result:', selectedVTOResult.id);
        const vtoResult = await removeVTOResultBackground(selectedVTOResult.id).unwrap();

        if (vtoResult?.removedBgImage?.url) {
          console.log('✅ VTO result background removed:', vtoResult.removedBgImage.url);

          // Update canvas to show the removed background VTO result
          if (isCanvasIntegrated) {
            await personImageState.setVirtualTryOnResult({
              image: vtoResult.removedBgImage.url,
              originalImage: activePersonImage?.image?.url || activePersonImage?.removedBgImage?.url,
              metadata: {
                isVirtualTryOnResult: true,
                vtoResultId: vtoResult.id
              }
            });
            console.log('✅ Canvas updated with removed background VTO result');
          }
        }

        // Also remove BG from person image if it doesn't have one yet
        // This ensures future VTO generations can use the removed BG version
        if (activePersonImage?.id && activePersonImage?.image?.url && !activePersonImage?.removedBgImage) {
          console.log('🔄 Also removing background from person image:', activePersonImage.id);
          try {
            // Compress in frontend before sending
            const compressedFile = await compressForBackgroundRemoval(activePersonImage.image.url);
            await removePersonBgFromUpload({
              file: compressedFile,
              personImageId: activePersonImage.id
            }).unwrap();
            console.log('✅ Person image background also removed');
            refetchImages?.();
          } catch (personBgError) {
            console.error('Failed to remove person image background:', personBgError);
            // Don't fail the whole operation
          }
        }
      } else {
        // No VTO result selected, remove BG from person image
        if (!activePersonImage?.id || !activePersonImage?.image?.url) {
          console.error('No active person image to remove background from');
          return;
        }

        console.log('🔄 Removing background from person image:', activePersonImage.id);

        // Compress image in frontend before sending to backend
        console.log('📦 Compressing image in frontend for background removal...');
        const compressedFile = await compressForBackgroundRemoval(activePersonImage.image.url);

        const result = await removePersonBgFromUpload({
          file: compressedFile,
          personImageId: activePersonImage.id
        }).unwrap();

        if (result?.personImage?.removedBgImage?.url) {
          console.log('✅ Person image background removed:', result.personImage.removedBgImage.url);

          // Update canvas with removed BG person image
          personImageState.setPersonImage(result.personImage.removedBgImage.url);

          // Refresh person image data
          await setActivePersonImage(result.personImage.id);
          refetchImages?.();
        }
      }

      // Refetch credits after any BG removal
      refetchCredits?.();
    } catch (error) {
      console.error('Background removal error:', error);
    } finally {
      setIsRemovingBg(false);
      onBgRemovalEnd?.();
    }
  };

  // Expose methods and state to parent via ref
  useImperativeHandle(ref, () => ({
    handleToggleBackground,
    handleRemoveBackground,
    canToggle,
    needsBgRemoval,
    useRemovedImage,
    isRemovingBg
  }));

  return (
    <div className="original-image-section">
      <div className="section-label">Wearer</div>
      <div className="image-container">
        {imageToShow ? (
          <div
            className="image-wrapper clickable"
            onClick={handleImageClick}
            title="Click to change person image"
          >
            <img
              src={imageToShow}
              alt="Original person"
              className="original-image"
            />
            {(isToggling || isTogglingPerson || isTogglingVTO || isRemovingVTOBg) && (
              <div className="loading-overlay">
                <div className="loading-spinner">
                  <Icon name="loading" loading color='teal' />
                </div>
              </div>
            )}
            {canToggle && (
              <button
                className="toggle-bg-btn-overlay"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleBackground();
                }}
                disabled={isToggling || isTogglingPerson || isTogglingVTO || isRemovingVTOBg}
                title={useRemovedImage ? 'Restore Background' : 'Use Removed Background'}
              >
                <Icon name={useRemovedImage ? 'undo' : 'eraser'} />
              </button>
            )}
          </div>
        ) : (
          <div
            className="no-image-placeholder clickable"
            onClick={handleImageClick}
            title="Click to add person image"
          >
            <span>No Wearer</span>
          </div>
        )}
      </div>
    </div>
  );
});

export default OriginalImageSection;